import { setUser } from '@sentry/react'
import { useCallback } from 'react'
import { useAsyncCallback } from 'react-async-hook'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import type { WebApiResponse } from ':/_types/webapi'
import { useApiSWR } from '~/api/swr/useApiSWR.ts'
import { tryLogoutIntercom } from '~/utils/intercom.ts'

import { apiFetchWorkspaceless } from '../../api/utils/apiFetch.ts'
import { trackEvent } from '../analytics/plausible.ts'
import type { FlashMessage } from '../flash-messages/types.ts'
import { createFlashMessage } from '../flash-messages/utils.ts'
import { LoginData } from '../workspaces/types.ts'

const LoginDataResponseBody = z.object({
  login_data: LoginData,
})

export function useLoginData() {
  return useApiSWR('/users/login_data.json', { out: LoginDataResponseBody })
}

export function useLoginUser() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { mutate } = useLoginData()

  const loginUser = useAsyncCallback<WebApiResponse<[]>, [email: string, password: string]>(
    async (email, password) => {
      const response = await apiFetchWorkspaceless('/user/login.json', {
        method: 'POST',
        body: {
          email,
          password,
        },
      })

      await mutate()

      const redirectQueryParam = params.get('redirect')
      if (response.meta.status === 'ok' && response.meta.redirect) {
        // if we have a redirect query param, forward it to the 2FA page so we can use it there
        navigate(
          `${response.meta.redirect}${redirectQueryParam ? `?redirect=${redirectQueryParam}` : ''}`,
        )
      }

      return response
    },
  )

  return loginUser
}

export function useSignUpUser() {
  const navigate = useNavigate()

  return useAsyncCallback<
    WebApiResponse<[]>,
    [email: string, confirm_field: string, password: string]
  >(async (email, confirm_field, password) => {
    const response: WebApiResponse<[]> = await apiFetchWorkspaceless('/user/signup.json', {
      method: 'POST',
      body: {
        email,
        confirm_field,
        password,
      },
    })

    if (response.meta.status === 'ok' && response.meta.redirect) {
      trackEvent('Sign up')
      navigate(response.meta.redirect)
    }

    return response
  })
}

export function useRequestForgotPasswordEmail() {
  return useAsyncCallback<WebApiResponse<[]>, [email: string]>((email) => {
    return apiFetchWorkspaceless('/user/forgot-password.json', {
      method: 'POST',
      body: {
        email,
      },
    })
  })
}

export function useResetPassword() {
  const navigate = useNavigate()
  return useAsyncCallback<
    WebApiResponse<[]>,
    [email: string, password: string, confirm_password: string, auth_key: string]
  >(async (email, password, confirm_password, auth_key) => {
    const res: WebApiResponse<[]> = await apiFetchWorkspaceless('/api/reset-password.json', {
      method: 'POST',
      body: {
        email,
        password,
        confirm_password,
        auth_key,
      },
    })

    if (res.meta.status === 'ok' && res.meta.redirect) {
      createFlashMessage({ text: res.meta.message, isError: false })
      navigate(res.meta.redirect)
    }

    return res
  })
}

export function useVerifyTwoFactorCode(key?: string) {
  const { mutate } = useLoginData()

  return useAsyncCallback<WebApiResponse<[]>, [code: string]>(async (code) => {
    const res = await apiFetchWorkspaceless('/api/auth-keys/verify.json', {
      method: 'POST',
      body: {
        code,
        key,
      },
    })

    // after verifying 2fa code, we're logged in, refresh login data
    await mutate()

    return res
  })
}

export function useResendTwoFactorEmail() {
  return useAsyncCallback<WebApiResponse<{ updatedAuthKey: string }>, [email: string]>((email) => {
    return apiFetchWorkspaceless(`/api/resend-2fa-email.json`, {
      method: 'POST',
      body: { email },
    })
  })
}

const logoutResponse = z.object({
  data: z.array(z.void()),
  meta: z.object({
    status: z.union([z.literal('ok'), z.literal('error')]),
    message: z.string().optional(),
    redirect: z.string().optional(),
  }),
})

export async function clearUserData() {
  // unset relevant localStorage items
  localStorage.removeItem('login_data')
  localStorage.removeItem('js-currentUser')
  localStorage.removeItem('currentWorkspaceSlug')

  // log out Intercom
  tryLogoutIntercom()

  // log out Sentry
  setUser(null)
}

export function useAfterLogout() {
  const navigate = useNavigate()

  return useCallback(
    async (redirectUrl = '/c/login/?loggedOut=1', message?: FlashMessage) => {
      await clearUserData()

      if (message) {
        createFlashMessage(message)
      }

      navigate(redirectUrl)
    },
    [navigate],
  )
}

export function useLogoutUser() {
  const afterLogout = useAfterLogout()

  return useAsyncCallback<
    WebApiResponse<[]>,
    [options?: { redirectUrl?: string; message?: FlashMessage }]
  >(async ({ redirectUrl, message } = {}) => {
    const response = await apiFetchWorkspaceless('/api/user/logout.json', {
      method: 'POST',
    })

    const data = logoutResponse.parse(response)

    await clearUserData()

    await afterLogout(
      redirectUrl ?? data.meta.redirect,
      message ?? {
        text: data.meta.message ?? 'You have been logged out',
        isError: data.meta.status === 'error',
      },
    )

    return response
  })
}
